import type { KhqrConfig } from '@telestore/contracts';
import { AppError, type Currency, CURRENCY_NUMERIC, type Money, toPayloadString } from '@telestore/shared';
import { appendCrc, encodeTemplate, encodeTlv, findField, parseTlv, sanitizeEmvText, verifyCrc } from './emv.js';

/**
 * KHQR payload generation (NBC Bakong profile of EMVCo MPM).
 *
 * This is the baseline of the whole payment ladder (CLAUDE.md §3): rung 1 is
 * "exact-amount QR + one-tap manual confirm", and every rung above it still
 * shows the customer this same code. A shop with nothing but a Bakong account
 * id can take money on day one, with zero paperwork.
 *
 * Tag layout:
 *   00  payload format indicator, always "01"
 *   01  point of initiation: "11" static, "12" dynamic (we are always dynamic —
 *       the amount is baked in, which is what makes reconciliation possible).
 *       VERIFIED 2026-08-26 against a real bank app: a payload built here shows
 *       a FIXED amount the payer cannot edit, and offers no currency toggle.
 *       No PayWay or Bakong contract is needed for that — those buy automatic
 *       CONFIRMATION (webhook / verification API), not a locked amount. The
 *       merchant's own printed standee is the opposite artifact: static, no
 *       tag 54, payer types amount and currency both (see the captures in
 *       `fixtures/aba-notifications.ts` — all five were paid that way).
 *   29  merchant account info, individual account
 *   30  merchant account info, registered merchant (used when the shop has a
 *       merchant id from its acquiring bank)
 *   52  merchant category code
 *   53  transaction currency, ISO 4217 numeric
 *   54  transaction amount
 *   58  country code, "KH"
 *   59  merchant name (Latin, <= 25 chars)
 *   60  merchant city (<= 15 chars)
 *   62  additional data — bill number carries our order code
 *   99  timestamp template: creation and expiry, epoch milliseconds
 *   63  CRC, always last
 */

export const KHQR_TAGS = {
  payloadFormat: '00',
  pointOfInitiation: '01',
  merchantAccountIndividual: '29',
  merchantAccountMerchant: '30',
  merchantCategoryCode: '52',
  transactionCurrency: '53',
  transactionAmount: '54',
  countryCode: '58',
  merchantName: '59',
  merchantCity: '60',
  additionalData: '62',
  timestamp: '99',
} as const;

/** Sub-tags inside 29/30. */
export const ACCOUNT_SUB = { bakongAccountId: '00', merchantId: '01', acquiringBank: '02' } as const;
/** Sub-tags inside 62. */
const ADDITIONAL_SUB = {
  billNumber: '01',
  mobileNumber: '02',
  storeLabel: '03',
  terminalLabel: '07',
  purpose: '08',
} as const;
/** Sub-tags inside 99. */
const TIMESTAMP_SUB = { createdAt: '00', expiresAt: '01' } as const;

/**
 * MCC (tag 52) is the same for every shop, vertical included. That is a
 * decision, not an unfinished one — an earlier note here promised F&B would
 * get 5812 "when that vertical ships", and it since has.
 *
 * The bank books a transfer under the category the MERCHANT registered with
 * it, not under the number we write into the payload. VILLA COFFEE's own ABA
 * standee carries 5814 (see `fixtures/khqr-payloads.ts`), and a QR we build
 * against that same account is accounted for the same way whatever sits here.
 * On our side tag 52 is decorative.
 *
 * Making it per-vertical would mean teaching `payment-core` what a vertical is
 * — nothing in `KhqrRequest` or `ChargeRequest` carries one — and §4 lists
 * deliberately where verticals may differ: catalog extension tables,
 * `fulfilment.ts`, UI presets, layout enums. The payment layer is not on that
 * list, and a decorative field is a poor reason to put it there.
 *
 * Revisit only if an acquirer starts caring — Bakong or PayWay onboarding
 * demanding a truthful category, or fees that differ by one.
 */
export const DEFAULT_MCC = '5999';

export interface KhqrRequest {
  config: KhqrConfig;
  /** The exact amount to be transferred, offset included. */
  amount: Money;
  /** Order code — lands in the bank's reference field, aiding manual audits. */
  billNumber?: string;
  storeLabel?: string;
  purpose?: string;
  createdAt?: Date;
  expiresAt?: Date | null;
}

export interface KhqrPayload {
  /** The raw string a QR renderer turns into pixels. */
  payload: string;
  /** MD5 of the payload — Bakong's own transaction lookup key. */
  amount: Money;
  merchantName: string;
  expiresAt: Date | null;
}

export function buildKhqrPayload(request: KhqrRequest): KhqrPayload {
  const { config, amount } = request;

  if (!config.bakong_account_id || !config.merchant_name) {
    throw new AppError(
      'khqr_config_missing',
      'This shop has not finished KHQR setup (Bakong account id and merchant name are required)',
    );
  }
  if (amount.amount <= 0) {
    throw new AppError('validation_failed', 'KHQR amount must be positive');
  }

  const merchantName = sanitizeEmvText(config.merchant_name, 25);
  const merchantCity = sanitizeEmvText(config.merchant_city || 'Phnom Penh', 15);
  if (merchantName === '') {
    throw new AppError(
      'khqr_config_missing',
      'Merchant name must contain Latin characters — Khmer-only names cannot be encoded in a KHQR payload',
    );
  }

  /**
   * Sub-tag 01 is what routes the money — see the routing tests.
   *
   * A payload with only sub-tag 00 is payable and undeliverable: the customer's
   * bank debits them against ABA's BIC, and no account is ever named. Refusing
   * to build one turns a silent loss into a setup screen the merchant can fix.
   */
  /**
   * Tag 30's sub-tag 01 is the acquiring bank's merchant id; tag 29's is the
   * account number. Only the tag-29 path is verified against a real transfer —
   * no registered merchant has tested this yet, so treat tag 30 as untested
   * rather than proven.
   */
  /**
   * The tag is read, not guessed. `account_tag` is what the merchant's own bank
   * printed; the `merchant_id` fallback exists only for configs written before
   * that field did, and removing it would silently move their money.
   */
  const accountTag = config.account_tag ?? (config.merchant_id ? '30' : '29');
  const isRegisteredMerchant = accountTag === '30';
  const routingValue = isRegisteredMerchant
    ? (config.merchant_id ?? config.account_number)
    : config.account_number;
  if (!routingValue) {
    throw new AppError(
      'khqr_config_missing',
      'This shop has not finished KHQR setup (the account number is what tells the bank where to send the money)',
    );
  }

  const accountFields: Array<{ id: string; value: string }> = [
    { id: ACCOUNT_SUB.bakongAccountId, value: config.bakong_account_id },
    { id: ACCOUNT_SUB.merchantId, value: routingValue },
  ];
  const bankLabel = config.bank_name ?? config.acquiring_bank;
  if (bankLabel) {
    accountFields.push({ id: ACCOUNT_SUB.acquiringBank, value: bankLabel });
  }

  const additionalFields: Array<{ id: string; value: string }> = [];
  if (request.billNumber) {
    additionalFields.push({
      id: ADDITIONAL_SUB.billNumber,
      value: sanitizeEmvText(request.billNumber, 25),
    });
  }
  if (request.storeLabel) {
    additionalFields.push({
      id: ADDITIONAL_SUB.storeLabel,
      value: sanitizeEmvText(request.storeLabel, 25),
    });
  }
  if (request.purpose) {
    additionalFields.push({
      id: ADDITIONAL_SUB.purpose,
      value: sanitizeEmvText(request.purpose, 25),
    });
  }

  const createdAt = request.createdAt ?? new Date();
  const expiresAt = request.expiresAt ?? null;
  const timestampFields: Array<{ id: string; value: string }> = [
    { id: TIMESTAMP_SUB.createdAt, value: String(createdAt.getTime()) },
  ];
  if (expiresAt) {
    timestampFields.push({ id: TIMESTAMP_SUB.expiresAt, value: String(expiresAt.getTime()) });
  }

  let payload = '';
  payload += encodeTlv(KHQR_TAGS.payloadFormat, '01');
  // Always dynamic: the amount is fixed, which is what lets an inbound bank
  // notification be matched back to exactly one order.
  payload += encodeTlv(KHQR_TAGS.pointOfInitiation, '12');
  payload += encodeTemplate(
    accountTag === '30' ? KHQR_TAGS.merchantAccountMerchant : KHQR_TAGS.merchantAccountIndividual,
    accountFields,
  );
  payload += encodeTlv(KHQR_TAGS.merchantCategoryCode, DEFAULT_MCC);
  payload += encodeTlv(KHQR_TAGS.transactionCurrency, CURRENCY_NUMERIC[amount.currency]);
  payload += encodeTlv(KHQR_TAGS.transactionAmount, toPayloadString(amount));
  payload += encodeTlv(KHQR_TAGS.countryCode, 'KH');
  payload += encodeTlv(KHQR_TAGS.merchantName, merchantName);
  payload += encodeTlv(KHQR_TAGS.merchantCity, merchantCity);
  if (additionalFields.length > 0) {
    payload += encodeTemplate(KHQR_TAGS.additionalData, additionalFields);
  }
  payload += encodeTemplate(KHQR_TAGS.timestamp, timestampFields);

  return {
    payload: appendCrc(payload),
    amount,
    merchantName,
    expiresAt,
  };
}

export interface DecodedKhqr {
  valid: boolean;
  amount: number | null;
  currency: Currency | null;
  merchantName: string | null;
  merchantCity: string | null;
  bakongAccountId: string | null;
  /**
   * Tag 29/30 sub-tag 01 — the field that actually routes the money.
   *
   * Surfaced because it is the one a merchant can get wrong invisibly, and
   * because the admin's "check my QR" tool has to be able to say "this code
   * pays account 015724098" rather than only "this code looks well formed".
   */
  accountNumber: string | null;
  billNumber: string | null;
  expiresAt: Date | null;
}

/**
 * Decoding exists for our own tests and for the admin's "check my QR" tool —
 * a merchant who mistypes their Bakong id should find out from us, not from a
 * customer whose transfer bounced.
 */
export function decodeKhqrPayload(payload: string): DecodedKhqr {
  const empty: DecodedKhqr = {
    valid: false,
    amount: null,
    currency: null,
    merchantName: null,
    merchantCity: null,
    bakongAccountId: null,
    accountNumber: null,
    billNumber: null,
    expiresAt: null,
  };

  let fields;
  try {
    fields = parseTlv(payload);
  } catch {
    return empty;
  }

  const currencyCode = findField(fields, KHQR_TAGS.transactionCurrency);
  const currency: Currency | null =
    currencyCode === CURRENCY_NUMERIC.USD ? 'USD' : currencyCode === CURRENCY_NUMERIC.KHR ? 'KHR' : null;

  const amountText = findField(fields, KHQR_TAGS.transactionAmount);
  const decimals = currency === 'KHR' ? 0 : 2;
  const amount =
    amountText === undefined ? null : Math.round(Number.parseFloat(amountText) * 10 ** decimals);

  const accountTemplate =
    findField(fields, KHQR_TAGS.merchantAccountMerchant) ??
    findField(fields, KHQR_TAGS.merchantAccountIndividual);
  const accountSubFields = accountTemplate ? parseTlv(accountTemplate) : null;
  const bakongAccountId = accountSubFields
    ? (findField(accountSubFields, ACCOUNT_SUB.bakongAccountId) ?? null)
    : null;
  const accountNumber = accountSubFields
    ? (findField(accountSubFields, ACCOUNT_SUB.merchantId) ?? null)
    : null;

  const additional = findField(fields, KHQR_TAGS.additionalData);
  const billNumber = additional
    ? (findField(parseTlv(additional), ADDITIONAL_SUB.billNumber) ?? null)
    : null;

  const timestamp = findField(fields, KHQR_TAGS.timestamp);
  const expiresText = timestamp
    ? findField(parseTlv(timestamp), TIMESTAMP_SUB.expiresAt)
    : undefined;

  return {
    valid: verifyCrc(payload),
    amount: amount !== null && Number.isFinite(amount) ? amount : null,
    currency,
    merchantName: findField(fields, KHQR_TAGS.merchantName) ?? null,
    merchantCity: findField(fields, KHQR_TAGS.merchantCity) ?? null,
    bakongAccountId,
    accountNumber,
    billNumber,
    expiresAt: expiresText ? new Date(Number.parseInt(expiresText, 10)) : null,
  };
}

/**
 * The label shown under the QR so a customer can sanity-check the payee before
 * transferring. Merchants asked for this during discovery: an unnamed QR reads
 * like a scam.
 */
export function payeeLabel(config: KhqrConfig): string {
  return config.account_label?.trim() || `${config.merchant_name} · ${config.bakong_account_id}`;
}
